(() => {
  if (customElements.get('wc-stories-carousel')) return;

  class WcStoriesCarousel extends HTMLElement {
    constructor() {
      super();
      this.videoCarousel = null;
      this.autoplayTimer = null;
      this.autoplayDelay = 5000;
      this.boundResize = this.debounce(this.handleResize.bind(this), 150);
      this.blockSelectHandler = this.handleBlockSelect.bind(this);
      this.prevHandler = this.handlePrevClick.bind(this);
      this.nextHandler = this.handleNextClick.bind(this);
      this.trackTransitionHandler = this.handleTrackTransitionEnd.bind(this);
      this.pointerDownHandler = this.handlePointerDown.bind(this);
      this.pointerMoveHandler = this.handlePointerMove.bind(this);
      this.pointerUpHandler = this.handlePointerUp.bind(this);
      this.pointerCancelHandler = this.handlePointerUp.bind(this);
      this.keydownHandler = this.handleKeydown.bind(this);
      this.visibilityHandler = this.handleVisibilityChange.bind(this);
      this.mouseEnterHandler = this.handleMouseEnter.bind(this);
      this.mouseLeaveHandler = this.handleMouseLeave.bind(this);
      this.videoPlayHandler = (event) => this.onVideoPlay(event.currentTarget);
      this.videoPauseHandler = (event) => this.onVideoPause(event.currentTarget);
      this.isHovering = false;
      this.pointerDownTrigger = null;
    }

    connectedCallback() {
      this.sectionElement = this.closest('[data-wc-stories-section]');
      this.videoViewport = this.querySelector('.wc-stories__video-carousel');
      this.videoTrack = this.querySelector('.wc-stories__video-track');
      this.enableLoop = this.dataset.loop === 'true';
      this.prevEl = this.querySelector('.wc-stories__nav--prev');
      this.nextEl = this.querySelector('.wc-stories__nav--next');
      this.scrollbarFill = this.querySelector('.wc-stories__scrollbar-fill');
      this.mobileBreakpoint = parseInt(this.dataset.mobileBreakpoint || '767', 10);
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.transitionMs = this.prefersReducedMotion ? 0 : 500;

      document.addEventListener('shopify:section:load', this.sectionLoadHandler = this.handleSectionLoad.bind(this));
      document.addEventListener('shopify:block:select', this.blockSelectHandler);
      document.addEventListener('visibilitychange', this.visibilityHandler);

      this.initVideoCarousel();
      window.addEventListener('resize', this.boundResize, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundResize);
      document.removeEventListener('shopify:section:load', this.sectionLoadHandler);
      document.removeEventListener('shopify:block:select', this.blockSelectHandler);
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.stopAutoplay();
      this.destroyVideoCarousel();
    }

    debounce(fn, wait) {
      let timer = null;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), wait);
      };
    }

    isMobile() {
      return window.innerWidth <= this.mobileBreakpoint;
    }

    resolveGap() {
      const raw = this.isMobile()
        ? this.dataset.spaceBetweenM
        : this.dataset.spaceBetween;
      const gap = parseInt(raw || '30', 10);
      return Number.isFinite(gap) ? gap : 30;
    }

    resolveInitialSlide(slideCount) {
      const parsed = parseInt(this.dataset.initialSlide || '2', 10);
      const index = Number.isFinite(parsed) ? parsed : 2;
      if (!slideCount || slideCount <= 1) return 0;
      return Math.min(Math.max(index, 0), slideCount - 1);
    }

    getInactiveScale() {
      const root = this.sectionElement || this;
      const styles = getComputedStyle(root);
      const raw = this.isMobile()
        ? styles.getPropertyValue('--slide-scale-inactive-m')
        : styles.getPropertyValue('--slide-scale-inactive');
      const scale = parseFloat(raw);
      return Number.isFinite(scale) ? scale : (this.isMobile() ? 0.92 : 0.882);
    }

    handleSectionLoad(event) {
      if (!this.sectionElement) return;
      if (event.detail.sectionId !== this.dataset.sectionId) return;
      this.stopAutoplay();
      this.destroyVideoCarousel();
      this.initVideoCarousel();
    }

    handleBlockSelect(event) {
      if (!this.sectionElement || !this.videoCarousel) return;
      if (event.detail.sectionId !== this.dataset.sectionId) return;

      const blockId = event.detail.blockId;
      const slides = Array.from(this.querySelectorAll('.wc-stories__video-slide:not([data-carousel-clone])'));
      const index = slides.findIndex((slide) => slide.dataset.blockId === blockId);

      if (index >= 0) {
        this.goToSlide(index);
        this.restartAutoplay();
      }
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
        return;
      }
      this.startAutoplay();
    }

    handleMouseEnter() {
      this.isHovering = true;
      this.stopAutoplay();
    }

    handleMouseLeave() {
      this.isHovering = false;
      this.startAutoplay();
    }

    handleResize() {
      if (!this.videoCarousel) return;
      window.requestAnimationFrame(() => {
        if (!this.videoCarousel) return;
        this.videoCarousel.measure();
        this.videoCarousel.goToVisual(this.videoCarousel.visualIndex, false);
        this.updateSlideScales();
      });
    }

    initVideoCarousel() {
      if (!this.videoViewport || !this.videoTrack || this.videoCarousel) return;

      const slides = Array.from(this.videoTrack.querySelectorAll('.wc-stories__video-slide:not([data-carousel-clone])'));
      if (slides.length <= 0) return;

      slides.forEach((slide, index) => {
        slide.dataset.slideIndex = String(index);
      });

      const slideCount = slides.length;
      const enableLoop = this.enableLoop && slideCount > 1;
      const initialSlide = this.resolveInitialSlide(slideCount);

      if (enableLoop) {
        const beforeFragment = document.createDocumentFragment();
        const afterFragment = document.createDocumentFragment();

        slides.forEach((slide) => {
          const beforeClone = slide.cloneNode(true);
          const afterClone = slide.cloneNode(true);
          beforeClone.setAttribute('data-carousel-clone', 'before');
          afterClone.setAttribute('data-carousel-clone', 'after');
          beforeClone.setAttribute('aria-hidden', 'true');
          afterClone.setAttribute('aria-hidden', 'true');
          beforeFragment.appendChild(beforeClone);
          afterFragment.appendChild(afterClone);
        });

        this.videoTrack.insertBefore(beforeFragment, this.videoTrack.firstChild);
        this.videoTrack.appendChild(afterFragment);
      }

      const visualIndex = enableLoop ? slideCount + initialSlide : initialSlide;

      this.videoCarousel = {
        slideCount,
        enableLoop,
        visualIndex,
        activeIndex: initialSlide,
        currentTranslate: 0,
        isDragging: false,
        isAnimating: false,
        dragStartX: 0,
        dragStartTranslate: 0,
        metrics: { slideWidth: 0, gap: 0, step: 0 },
        measure: () => {
          const gap = this.resolveGap();
          const firstSlide = this.videoTrack.querySelector('.wc-stories__video-slide');
          const slideWidth = firstSlide ? firstSlide.offsetWidth : 0;
          this.videoCarousel.metrics = {
            slideWidth,
            gap,
            step: slideWidth + gap
          };
        },
        getTranslateForVisual: (visualIndex) => {
          const { slideWidth, step } = this.videoCarousel.metrics;
          if (!slideWidth) return 0;
          const viewportWidth = this.videoViewport.clientWidth;
          const slideCenter = visualIndex * step + slideWidth / 2;
          return viewportWidth / 2 - slideCenter;
        },
        getRealIndex: () => {
          const { slideCount, enableLoop, visualIndex } = this.videoCarousel;
          if (!enableLoop) return visualIndex;
          return ((visualIndex - slideCount) % slideCount + slideCount) % slideCount;
        },
        goToVisual: (visualIndex, animate = true) => {
          const carousel = this.videoCarousel;
          if (!carousel) return;

          if (!carousel.enableLoop) {
            carousel.visualIndex = Math.min(Math.max(visualIndex, 0), carousel.slideCount - 1);
          } else {
            carousel.visualIndex = visualIndex;
          }

          carousel.activeIndex = carousel.getRealIndex();
          carousel.currentTranslate = carousel.getTranslateForVisual(carousel.visualIndex);

          this.videoTrack.style.transition = animate && this.transitionMs
            ? `transform ${this.transitionMs}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
            : 'none';
          this.videoTrack.style.transform = `translate3d(${carousel.currentTranslate}px, 0, 0)`;

          if (!animate || !this.transitionMs) {
            this.setScrollbarTransition(0);
            this.normalizeLoopPosition();
            this.updateSlideScalesToVisual(carousel.visualIndex);
            this.updateScrollbar();
            this.manageVideos();
            this.updateNavState();
          } else {
            carousel.isAnimating = true;
            this.setSlideTransition(this.transitionMs);
            this.setScrollbarTransition(this.transitionMs);
            this.updateSlideScalesToVisual(carousel.visualIndex);
            this.updateScrollbar();
          }
        }
      };

      this.videoCarousel.measure();
      this.bindVideoCarouselEvents();
      window.requestAnimationFrame(() => {
        if (!this.videoCarousel) return;
        this.videoCarousel.measure();
        this.videoCarousel.goToVisual(visualIndex, false);
      });

      this.videoTrack.querySelectorAll('.wc-stories__video-trigger').forEach((trigger) => {
        trigger.style.transitionProperty = 'transform';
        trigger.style.transitionTimingFunction = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      });

      this.bindVideoPlaybackEvents();
      this.startAutoplay();
    }

    bindVideoPlaybackEvents() {
      this.querySelectorAll('[data-wc-stories-video]').forEach((video) => {
        video.addEventListener('play', this.videoPlayHandler);
        video.addEventListener('pause', this.videoPauseHandler);
      });
    }

    unbindVideoPlaybackEvents() {
      if (!this.videoPlayHandler || !this.videoPauseHandler) return;

      this.querySelectorAll('[data-wc-stories-video]').forEach((video) => {
        video.removeEventListener('play', this.videoPlayHandler);
        video.removeEventListener('pause', this.videoPauseHandler);
      });
    }

    onVideoPlay(video) {
      this.setVideoControls(video, true);
    }

    onVideoPause(video) {
      const activeIndex = this.getActiveIndex();
      const slide = video.closest('.wc-stories__video-slide');
      const slideIndex = parseInt(slide?.dataset.slideIndex ?? '-1', 10);
      if (slideIndex !== activeIndex) {
        this.setVideoControls(video, false);
      }
    }

    setVideoControls(video, show) {
      if (!video) return;

      const trigger = video.closest('.wc-stories__video-trigger');
      if (show) {
        video.setAttribute('controls', '');
        video.classList.add('is-controls-visible');
        trigger?.classList.add('is-video-playing');
        return;
      }

      video.removeAttribute('controls');
      video.classList.remove('is-controls-visible');
      trigger?.classList.remove('is-video-playing');
    }

    isVideoControlTarget(target) {
      return Boolean(target?.closest('.wc-stories__video.is-controls-visible'));
    }

    bindVideoCarouselEvents() {
      this.videoViewport.addEventListener('pointerdown', this.pointerDownHandler);
      this.videoViewport.addEventListener('pointermove', this.pointerMoveHandler);
      this.videoViewport.addEventListener('pointerup', this.pointerUpHandler);
      this.videoViewport.addEventListener('pointercancel', this.pointerCancelHandler);
      this.videoViewport.addEventListener('keydown', this.keydownHandler);
      this.videoViewport.addEventListener('mouseenter', this.mouseEnterHandler);
      this.videoViewport.addEventListener('mouseleave', this.mouseLeaveHandler);
      this.videoTrack.addEventListener('transitionend', this.trackTransitionHandler);

      if (this.prevEl) this.prevEl.addEventListener('click', this.prevHandler);
      if (this.nextEl) this.nextEl.addEventListener('click', this.nextHandler);
    }

    unbindVideoCarouselEvents() {
      if (!this.videoViewport) return;

      this.videoViewport.removeEventListener('pointerdown', this.pointerDownHandler);
      this.videoViewport.removeEventListener('pointermove', this.pointerMoveHandler);
      this.videoViewport.removeEventListener('pointerup', this.pointerUpHandler);
      this.videoViewport.removeEventListener('pointercancel', this.pointerCancelHandler);
      this.videoViewport.removeEventListener('keydown', this.keydownHandler);
      this.videoViewport.removeEventListener('mouseenter', this.mouseEnterHandler);
      this.videoViewport.removeEventListener('mouseleave', this.mouseLeaveHandler);

      if (this.videoTrack) {
        this.videoTrack.removeEventListener('transitionend', this.trackTransitionHandler);
      }

      if (this.prevEl) this.prevEl.removeEventListener('click', this.prevHandler);
      if (this.nextEl) this.nextEl.removeEventListener('click', this.nextHandler);
    }

    handleTrackTransitionEnd(event) {
      if (!this.videoCarousel || event.target !== this.videoTrack || event.propertyName !== 'transform') return;

      this.videoCarousel.isAnimating = false;
      this.setSlideTransition(0);
      this.normalizeLoopPosition();
      this.updateSlideScalesToVisual(this.videoCarousel.visualIndex);
      this.updateScrollbar();
      this.manageVideos();
      this.updateNavState();
      this.setSlideTransition(this.transitionMs);
    }

    normalizeLoopPosition() {
      const carousel = this.videoCarousel;
      if (!carousel?.enableLoop) return;

      const { slideCount, visualIndex } = carousel;
      let nextVisual = visualIndex;

      if (visualIndex >= slideCount * 2) {
        nextVisual = visualIndex - slideCount;
      } else if (visualIndex < slideCount) {
        nextVisual = visualIndex + slideCount;
      }

      if (nextVisual === visualIndex) return;

      carousel.visualIndex = nextVisual;
      carousel.activeIndex = carousel.getRealIndex();
      carousel.currentTranslate = carousel.getTranslateForVisual(nextVisual);
      this.videoTrack.style.transition = 'none';
      this.videoTrack.style.transform = `translate3d(${carousel.currentTranslate}px, 0, 0)`;
    }

    activateSlide(slide) {
      if (!slide || !this.videoCarousel) return;

      const index = parseInt(slide.dataset.slideIndex || '-1', 10);
      if (index < 0) return;

      if (index === this.getActiveIndex()) {
        this.toggleActiveVideo();
        this.restartAutoplay();
        return;
      }

      this.goToSlide(index);
      this.restartAutoplay();
    }

    handlePointerDown(event) {
      const carousel = this.videoCarousel;
      if (!carousel || carousel.slideCount <= 1) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (this.isVideoControlTarget(event.target)) return;
      this.stopAutoplay();

      this.pointerDownTrigger = event.target.closest('.wc-stories__video-trigger');
      carousel.isDragging = false;
      carousel.dragStartX = event.clientX;
      carousel.dragStartTranslate = carousel.currentTranslate;

      this.videoViewport.setPointerCapture(event.pointerId);
      this.videoViewport.classList.add('is-dragging');
    }

    handlePrevClick() {
      this.stepSlide(-1);
      this.restartAutoplay();
    }

    handleNextClick() {
      this.stepSlide(1);
      this.restartAutoplay();
    }

    stepSlide(direction) {
      const carousel = this.videoCarousel;
      if (!carousel || carousel.slideCount <= 1) return;

      if (carousel.enableLoop) {
        carousel.goToVisual(carousel.visualIndex + direction, true);
        return;
      }

      const nextIndex = Math.min(
        Math.max(carousel.activeIndex + direction, 0),
        carousel.slideCount - 1
      );
      carousel.goToVisual(nextIndex, true);
    }

    goToSlide(index) {
      const carousel = this.videoCarousel;
      if (!carousel) return;

      let targetVisual = index;
      if (carousel.enableLoop) {
        const base = carousel.slideCount + index;
        const prev = base - carousel.slideCount;
        const next = base + carousel.slideCount;
        const current = carousel.visualIndex;

        targetVisual = [prev, base, next].reduce((closest, candidate) => (
          Math.abs(candidate - current) < Math.abs(closest - current) ? candidate : closest
        ), base);
      }

      carousel.goToVisual(targetVisual, true);
    }

    getActiveIndex() {
      return this.videoCarousel?.activeIndex ?? 0;
    }

    handleKeydown(event) {
      if (!this.videoCarousel) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.stepSlide(-1);
        this.restartAutoplay();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.stepSlide(1);
        this.restartAutoplay();
      }
    }

    handlePointerMove(event) {
      const carousel = this.videoCarousel;
      if (!carousel || !this.videoViewport.hasPointerCapture(event.pointerId)) return;

      const delta = event.clientX - carousel.dragStartX;
      if (!carousel.isDragging && Math.abs(delta) < 6) return;

      if (!carousel.isDragging) {
        carousel.isDragging = true;
        this.setSlideTransition(0);
      }

      carousel.currentTranslate = carousel.dragStartTranslate + delta;
      this.videoTrack.style.transition = 'none';
      this.videoTrack.style.transform = `translate3d(${carousel.currentTranslate}px, 0, 0)`;
      this.updateSlideScales();
    }

    handlePointerUp(event) {
      const carousel = this.videoCarousel;
      if (!carousel || !this.videoViewport.hasPointerCapture(event.pointerId)) return;

      this.videoViewport.releasePointerCapture(event.pointerId);
      this.videoViewport.classList.remove('is-dragging');

      const wasDragging = carousel.isDragging;
      carousel.isDragging = false;

      if (wasDragging) {
        const delta = event.clientX - carousel.dragStartX;
        const threshold = Math.max((carousel.metrics.slideWidth || 0) * 0.18, 24);

        if (delta < -threshold) {
          this.stepSlide(1);
        } else if (delta > threshold) {
          this.stepSlide(-1);
        } else {
          carousel.goToVisual(carousel.visualIndex, true);
        }

        this.pointerDownTrigger = null;
        this.startAutoplay();
        return;
      }

      const hitTarget = document.elementFromPoint(event.clientX, event.clientY);
      if (this.isVideoControlTarget(hitTarget)) {
        this.pointerDownTrigger = null;
        this.startAutoplay();
        return;
      }

      const trigger = hitTarget?.closest('.wc-stories__video-trigger')
        || event.target.closest('.wc-stories__video-trigger')
        || this.pointerDownTrigger;
      this.pointerDownTrigger = null;

      if (trigger) {
        const slide = trigger.closest('.wc-stories__video-slide');
        this.activateSlide(slide);
      }

      this.startAutoplay();
    }

    startAutoplay() {
      const carousel = this.videoCarousel;
      if (!carousel || carousel.slideCount <= 1 || this.autoplayTimer || document.hidden || this.isHovering) return;

      this.autoplayTimer = window.setInterval(() => {
        if (!this.videoCarousel || this.videoCarousel.isDragging || this.videoCarousel.isAnimating) return;
        this.stepSlide(1);
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    updateSlideScales() {
      if (!this.videoCarousel || !this.videoViewport || !this.videoTrack) return;

      const inactiveScale = this.getInactiveScale();
      const viewportRect = this.videoViewport.getBoundingClientRect();
      const centerX = viewportRect.left + viewportRect.width / 2;
      const step = this.videoCarousel.metrics.step || 1;
      const slides = Array.from(this.videoTrack.querySelectorAll('.wc-stories__video-slide'));
      const scales = [];
      let anchorIndex = 0;
      let minProgress = Infinity;

      slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const slideCenter = rect.left + rect.width / 2;
        const progress = Math.min(Math.abs(slideCenter - centerX) / step, 1);
        const scale = inactiveScale + (1 - inactiveScale) * (1 - progress);

        scales.push(scale);

        if (progress < minProgress) {
          minProgress = progress;
          anchorIndex = index;
        }
      });

      this.applySlideScales(slides, scales, anchorIndex);
    }

    updateSlideScalesToVisual(visualIndex) {
      if (!this.videoCarousel || !this.videoTrack) return;

      const inactiveScale = this.getInactiveScale();
      const slides = Array.from(this.videoTrack.querySelectorAll('.wc-stories__video-slide'));
      if (!slides.length) return;

      const anchorIndex = Math.min(Math.max(visualIndex, 0), slides.length - 1);
      const scales = slides.map((_, index) => {
        const progress = Math.min(Math.abs(index - anchorIndex), 1);
        return inactiveScale + (1 - inactiveScale) * (1 - progress);
      });

      this.applySlideScales(slides, scales, anchorIndex);
    }

    applySlideScales(slides, scales, anchorIndex) {
      const slideWidth = this.videoCarousel?.metrics.slideWidth || 0;
      const translateX = new Array(slides.length).fill(0);

      for (let i = anchorIndex; i < slides.length - 1; i += 1) {
        translateX[i + 1] = translateX[i] + slideWidth * ((scales[i] + scales[i + 1]) / 2 - 1);
      }
      for (let i = anchorIndex; i > 0; i -= 1) {
        translateX[i - 1] = translateX[i] - slideWidth * ((scales[i - 1] + scales[i]) / 2 - 1);
      }

      slides.forEach((slide, index) => {
        slide.style.zIndex = String(Math.round(scales[index] * 100));

        const trigger = slide.querySelector('.wc-stories__video-trigger');
        if (!trigger) return;
        trigger.style.transform = `translateX(${translateX[index]}px) scale(${scales[index]})`;
      });
    }

    setSlideTransition(duration) {
      const ms = `${duration}ms`;
      this.videoTrack?.querySelectorAll('.wc-stories__video-trigger').forEach((trigger) => {
        trigger.style.transitionDuration = ms;
      });
    }

    setScrollbarTransition(duration) {
      if (!this.scrollbarFill) return;

      const ms = `${duration}ms`;
      this.scrollbarFill.style.transition = duration
        ? `width ${ms} cubic-bezier(0.25, 0.46, 0.45, 0.94)`
        : 'none';
    }

    updateScrollbar() {
      if (!this.scrollbarFill || !this.videoCarousel) return;

      const slideCount = this.videoCarousel.slideCount;
      if (slideCount <= 1) {
        this.scrollbarFill.style.left = '0';
        this.scrollbarFill.style.width = '100%';
        return;
      }

      const activeIndex = this.getActiveIndex();
      const widthPercent = ((activeIndex + 1) / slideCount) * 100;

      this.scrollbarFill.style.left = '0';
      this.scrollbarFill.style.width = `${widthPercent}%`;
    }

    updateNavState() {
      if (!this.videoCarousel || this.videoCarousel.enableLoop) {
        if (this.prevEl) this.prevEl.disabled = false;
        if (this.nextEl) this.nextEl.disabled = false;
        return;
      }

      const { activeIndex, slideCount } = this.videoCarousel;
      if (this.prevEl) this.prevEl.disabled = activeIndex <= 0;
      if (this.nextEl) this.nextEl.disabled = activeIndex >= slideCount - 1;
    }

    manageVideos() {
      if (!this.videoCarousel) return;

      const activeIndex = this.getActiveIndex();

      this.querySelectorAll('[data-wc-stories-video]').forEach((video) => {
        const slide = video.closest('.wc-stories__video-slide');
        const slideIndex = parseInt(slide?.dataset.slideIndex ?? '-1', 10);
        const isActive = slideIndex === activeIndex;

        if (!isActive || this.prefersReducedMotion) {
          video.pause();
          if (!isActive) {
            video.currentTime = 0;
            this.setVideoControls(video, false);
          }
          return;
        }

        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      });
    }

    toggleActiveVideo() {
      const activeIndex = this.getActiveIndex();
      const videos = Array.from(this.querySelectorAll('[data-wc-stories-video]'));
      const video = videos.find((item) => {
        const slide = item.closest('.wc-stories__video-slide');
        return parseInt(slide?.dataset.slideIndex ?? '-1', 10) === activeIndex;
      });

      if (!video) return;

      if (video.paused) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } else {
        video.pause();
      }
    }

    destroyVideoCarousel() {
      this.stopAutoplay();
      this.querySelectorAll('[data-wc-stories-video]').forEach((video) => {
        video.pause();
        this.setVideoControls(video, false);
      });
      this.unbindVideoPlaybackEvents();
      this.unbindVideoCarouselEvents();

      if (this.videoTrack) {
        this.videoTrack.querySelectorAll('[data-carousel-clone]').forEach((clone) => clone.remove());
        this.videoTrack.style.transition = '';
        this.videoTrack.style.transform = '';
      }

      this.videoCarousel = null;
    }
  }

  customElements.define('wc-stories-carousel', WcStoriesCarousel);
})();
